import React, { useEffect, useRef, useState, useCallback } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./MenuBook.css";
// import flipSound from "../components/audio/flipSound.mp3";
import apiClient from "../apis/apiClient";

const api = apiClient;

const FlipBook = () => {
  const flipBookRef = useRef(null);
  const audioRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("speisekarte");

  // Define category order with more flexible matching for Speisekarte
  const desiredOrder = [
    { keys: ["suppen", "soups"], label: "SUPPEN" },
    { keys: ["salate", "salads"], label: "SALATE" },
    { keys: ["vorspeisen", "starters", "appetizers"], label: "VORSPEISEN" },
    { keys: ["indische brotspezialitäten", "indian bread"], label: "INDISCHE BROTSPEZIALITÄTEN" },
    { keys: ["vegetarisch", "vegetarian"], label: "VEGETARISCH", description: "Inklusive Basmati Reis" },
    { keys: ["hähnchengerichte", "chicken dishes"], label: "HÄHNCHENGERICHTE", description: "Inklusive Basmati Reis" },
    { keys: ["lammgerichte", "lamb dishes"], label: "LAMMGERICHTE", description: "Inklusive Basmati Reis" },
    { keys: ["fischgerichte", "fish dishes"], label: "FISCHGERICHTE", description: "Inklusive Basmati Reis" },
    { keys: ["pasta", "noodles"], label: "PASTA" },
    { keys: ["tandoori spezialitäten", "tandoori specials"], label: "TANDOORI SPEZIALITÄTEN", description: "Tandoori Gerichte werden zubereitet, indem sie stundenlang in aromatischen Marinaden eingelegt und anschließend im Tandoori Ofen gegrillt werden. Mit Salat, Naan und Basmati Reis" },
    { keys: ["reis spezialitäten", "rice specials"], label: "REIS SPEZIALITÄTEN" },
    { keys: ["kinder gerichte", "kinder", "kids"], label: "KINDER GERICHTE" },
    { keys: ["nachspeisen", "desserts"], label: "NACHSPEISEN" },
    { keys: ["indische getränke", "indian drinks"], label: "INDISCHE GETRÄNKE" },
    { keys: ["heiße getränke", "hot drinks"], label: "HEIßE GETRÄNKE" }
  ];

  // Define category order with more flexible matching for Mittagskarte
  const desiredOrderMittag = [
    { keys: ["vegetarisch", "vegetarian"], label: "VEGETARISCH", description: "Alle Gerichte auf Anfrage auch vegan möglich. Zu allen Gerichten servieren wir Basmati-Reis und je nach Wahl eine Suppe oder Salat." },
    { keys: ["hähnchengerichte", "chicken dishes", "chicken"], label: "HÄHNCHENGERICHTE", description: "Zu allen Gerichten servieren wir Basmati-Reis und je nach Wahl eine Suppe oder Salat." },
    { keys: ["lammgerichte", "lamb dishes", "lamb"], label: "LAMMGERICHTE", description: "Zu allen Gerichten servieren wir Basmati-Reis und je nach Wahl eine Suppe oder Salat." },
    { keys: ["indisches brot", "brot", "bread"], label: "INDISCHES BROT" },
    { keys: ["salat", "salate", "salad", "salads"], label: "SALAT" },
    { keys: ["kinder gerichte", "kinder", "kids"], label: "KINDER GERICHTE" }
  ];

  // Fetch categories & menu items
  const fetchMenuData = useCallback(async () => {
    try {
      setLoading(true);
      const [categoriesRes, menuItemsRes] = await Promise.all([
        api.get("/categories"),
        api.get("/dishes"),
      ]);

      if (!categoriesRes.data || !menuItemsRes.data) {
        throw new Error("Invalid data format from API");
      }

      setCategories(categoriesRes.data);
      setMenuItems(menuItemsRes.data);
    } catch (error) {
      console.error("Error fetching menu data:", error);
      setError("Failed to load menu. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMenuData();
  }, [fetchMenuData]);

  // Set page to cover when loading completes or active tab changes
  useEffect(() => {
    if (!loading && flipBookRef.current) {
      flipBookRef.current.style.setProperty('--current-page', '0');
    }
  }, [loading, activeTab]);

  // Page flip effect
  useEffect(() => {
    if (loading) return;
    const bookElement = flipBookRef.current;
    if (!bookElement) return;

    const audio = audioRef.current;
    const pageElements = Array.from(bookElement.querySelectorAll(".flip-page"));

    const handleClick = (index, event) => {
      if (event.target.closest("a")) return;
      if (audio) {
        audio.currentTime = 0;
        audio.play().catch(() => { });
      }
      const current = event.target.closest(".page-back") ? index : index + 1;
      bookElement.style.setProperty("--current-page", current.toString());
    };

    pageElements.forEach((page, index) => {
      page.style.setProperty("--page-index", index.toString());
      page.addEventListener("click", (e) => handleClick(index, e));
    });

    return () => {
      pageElements.forEach((page, index) => {
        page.removeEventListener("click", (e) => handleClick(index, e));
      });
    };
  }, [loading, activeTab]);

  // Helper to identify Mittag categories
  const isMittagCategory = (category) => {
    const name = category.name.toLowerCase();
    return name.includes("mittag") || name.includes("lunch");
  };

  // Find category match with multiple possible keys
  const findCategoryMatch = (categoryName, orderList) => {
    const lowerName = categoryName.toLowerCase().trim();
    return orderList.find(orderItem =>
      orderItem.keys.some(key => lowerName.includes(key))
    );
  };

  // Filter categories and items based on activeTab
  const currentCategories = categories.filter(category => {
    const isMittag = isMittagCategory(category);
    return activeTab === "mittagskarte" ? isMittag : !isMittag;
  });

  const orderList = activeTab === "mittagskarte" ? desiredOrderMittag : desiredOrder;

  // Group items by category with flexible matching
  const groupedItems = {};
  currentCategories.forEach(category => {
    const match = findCategoryMatch(category.name, orderList);
    const items = menuItems.filter(item =>
      item.category?.id === category.id || item.category_id === category.id
    );

    if (match) {
      if (!groupedItems[match.label]) {
        groupedItems[match.label] = {
          items: [],
          description: match.description || ""
        };
      }
      groupedItems[match.label].items = [
        ...groupedItems[match.label].items,
        ...items
      ];
    } else {
      // Clean prefix for unmatched display
      const cleanedLabel = category.name
        .replace(/^(mittagskarte\s*-\s*|mittags\s*-\s*|mittagskarte\s+|mittags\s+)/i, "")
        .toUpperCase()
        .trim();

      if (items.length > 0) {
        if (!groupedItems[cleanedLabel]) {
          groupedItems[cleanedLabel] = {
            items: [],
            description: ""
          };
        }
        groupedItems[cleanedLabel].items = [
          ...groupedItems[cleanedLabel].items,
          ...items
        ];
      }
    }
  });

  const formatPrice = (price) => `${parseFloat(price).toFixed(2)} €`;

  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading menu...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100">
        <p className="text-danger">⚠️ {error}</p>
        <button className="btn btn-primary mt-2" onClick={fetchMenuData}>Retry</button>
      </div>
    );
  }

  // Render category block with optional limit and description hiding
  const renderCategory = (label, limit = null, hideDescription = false) => {
    const data = groupedItems[label];
    if (!data) return null;

    // Apply limit if specified
    const itemsToRender = limit ? data.items.slice(0, limit) : data.items;

    return (
      <React.Fragment key={label}>
        <h2 className="section-titles">{label}</h2>
        {data.description && !hideDescription && <p className="category-description">{data.description}</p>}
        {itemsToRender.length > 0 ? (
          itemsToRender.map((item) => (
            <div className="menu-item" key={item.dishid}>
              <div className="d-flex justify-content-between align-items-baseline">
                <span className="item-names fw-bold">{item.dishname}</span>
                <span className="price fw-bold">{formatPrice(item.dishprice)}</span>
              </div>
              {item.dishdescription && (
                <p className="item-description mb-0">{item.dishdescription}</p>
              )}
            </div>
          ))
        ) : (
          <p className="empty-category">No items available</p>
        )}
      </React.Fragment>
    );
  };

  const renderAllergensMittag = () => (
    <div className="allergen-info mt-3 pt-2" style={{ borderTop: "1px dashed rgba(212, 165, 116, 0.4)" }}>
      <h3 className="allergen-title fw-bold text-center mb-1" style={{ fontSize: "0.75rem", color: "#3d5a45" }}>Allergene:</h3>
      <div className="row g-1">
        <div className="col-6">
          <ul className="list-unstyled mb-0" style={{ fontSize: "0.58rem", fontFamily: "'Times New Roman', serif", color: "#2c2c2c", paddingLeft: "4px", lineHeight: "1.3" }}>
            <li className="mb-0"><strong>A:</strong> enthält Ei/-Erzeugnisse</li>
            <li className="mb-0"><strong>B:</strong> enthält Erdnüsse/-Erzeugnisse</li>
            <li className="mb-0"><strong>C:</strong> enthält Fisch/-Erzeugnisse</li>
            <li className="mb-0"><strong>D:</strong> enthält glutenhaltige/s Getreide/-Erzeugnisse</li>
            <li className="mb-0"><strong>E:</strong> enthält Krebstiere/-Erzeugnisse</li>
            <li className="mb-0"><strong>F:</strong> enthält Lupine/-nerzeugnisse</li>
            <li className="mb-0"><strong>G:</strong> enthält Milch/-Erzeugnisse (laktosehaltig)</li>
          </ul>
        </div>
        <div className="col-6">
          <ul className="list-unstyled mb-0" style={{ fontSize: "0.58rem", fontFamily: "'Times New Roman', serif", color: "#2c2c2c", paddingLeft: "4px", lineHeight: "1.3" }}>
            <li className="mb-0"><strong>H:</strong> enthält Schalenfrüchte/Nüsse bzw. Nusserzeugnisse</li>
            <li className="mb-0"><strong>I:</strong> enthält Schwefeloxyd/Sulfite</li>
            <li className="mb-0"><strong>J:</strong> enthält Sellerie/-Erzeugnisse</li>
            <li className="mb-0"><strong>K:</strong> enthält Senf/-Erzeugnisse</li>
            <li className="mb-0"><strong>L:</strong> enthält Sesamsamen/-Erzeugnisse</li>
            <li className="mb-0"><strong>M:</strong> enthält Sojabohnen/-Erzeugnisse</li>
            <li className="mb-0"><strong>N:</strong> enthält Weichtiere/-Erzeugnisse</li>
          </ul>
        </div>
      </div>
      <p className="disclaimer text-center mt-2 mb-0" style={{ fontSize: "0.52rem", fontStyle: "italic", color: "#555" }}>
        Kennzeichnungspflichtige.
      </p>
    </div>
  );

  const renderAllergensAndAdditives = () => (
    <div className="allergen-info">
      <h3 className="allergen-title fw-bold">Allergene:</h3>
      <div className="row">
        <div className="col-6">
          <ul className="allergen-list list-unstyled">
            <li><strong>A:</strong> enthält Ei/-Erzeugnisse</li>
            <li><strong>B:</strong> enthält Erdnüsse/-Erzeugnisse</li>
            <li><strong>C:</strong> enthält Fisch/-Erzeugnisse</li>
            <li><strong>D:</strong> enthält glutenhaltige/s Getreide/-Erzeugnisse</li>
            <li><strong>E:</strong> enthält Krebstiere/-Erzeugnisse</li>
            <li><strong>F:</strong> enthält Lupine/-Erzeugnisse</li>
            <li><strong>G:</strong> enthält Milch/-Erzeugnisse (laktosehaltig)</li>
          </ul>
        </div>
        <div className="col-6">
          <ul className="allergen-list list-unstyled">
            <li><strong>H:</strong> enthält Schalenfrüchte/Nüsse bzw. Nusserzeugnisse</li>
            <li><strong>I:</strong> enthält Schwefeloxyd/Sulfite</li>
            <li><strong>J:</strong> enthält Sellerie/-Erzeugnisse</li>
            <li><strong>K:</strong> enthält Senf/-Erzeugnisse</li>
            <li><strong>L:</strong> enthält Sesamsamen/-Erzeugnisse</li>
            <li><strong>M:</strong> enthält Sojabohnen/-Erzeugnisse</li>
            <li><strong>N:</strong> enthält Weichtiere/-Erzeugnisse</li>
          </ul>
        </div>
      </div>

      <h3 className="additives-title fw-bold mt-3">Zusatzstoffe und Zutaten:</h3>
      <div className="row">
        <div className="col-4">
          <ul className="additives-list list-unstyled">
            <li><strong>1:</strong> alkoholhaltig</li>
            <li><strong>2:</strong> chininhaltig</li>
            <li><strong>3:</strong> enthält Aspartam</li>
            <li><strong>4:</strong> enthalteine Phenylalaninquelle</li>
            <li><strong>5:</strong> enthält Süßholz</li>
            <li><strong>6:</strong> geschwärzt</li>
            <li><strong>7:</strong> geschwefelt</li>
            <li><strong>8:</strong> gewachst</li>
            <li><strong>9:</strong> kann bei übermäßigem Verzehr abführend wirken</li>
          </ul>
        </div>
        <div className="col-4">
          <ul className="additives-list list-unstyled">
            <li><strong>10:</strong> koffeinhaltig</li>
            <li><strong>11:</strong> mit Antioxidationsmittel</li>
            <li><strong>12:</strong> mit Azofarbstoffen</li>
            <li><strong>13:</strong> mit Farbstoff</li>
            <li><strong>14:</strong> mit Geschmacksverstärker</li>
            <li><strong>15:</strong> mit Konservierungsstoff</li>
            <li><strong>16:</strong> mit Nitrat</li>
            <li><strong>17:</strong> mit Nitritpökelsalz</li>
            <li><strong>18:</strong> mit Säuerungsmittel</li>
          </ul>
        </div>
        <div className="col-4">
          <ul className="additives-list list-unstyled">
            <li><strong>19:</strong> mit Phosphat</li>
            <li><strong>20:</strong> mit Süßungsmittel(n)</li>
            <li><strong>21:</strong> mit einer Zuckerart und Süßungsmittel(n)</li>
            <li><strong>22:</strong> Sulfite</li>
            <li><strong>23:</strong> mit Milcheiweiß</li>
            <li><strong>24:</strong> mit Stabilisatoren</li>
            <li><strong>25:</strong> mit Taurin</li>
            <li><strong>26:</strong> mit erhöhtem Koffeingehalt</li>
            <li><strong>51:</strong> Chinin</li>
          </ul>
        </div>
      </div>

      <p className="disclaimer mt-3 text-center">Änderungen und Irrtümer vorbehalten</p>
    </div>
  );

  return (
    <div className="total">
      {/* Tab Selector */}
      <div className="menu-tabs-container">
        <button
          className={`menu-tab-button ${activeTab === "speisekarte" ? "active" : ""}`}
          onClick={() => setActiveTab("speisekarte")}
        >
          Speisekarten
        </button>
        <button
          className={`menu-tab-button ${activeTab === "mittagskarte" ? "active" : ""}`}
          onClick={() => setActiveTab("mittagskarte")}
        >
          Mittagskarte
        </button>
      </div>


      <div className="flip-book-container" ref={flipBookRef} key={activeTab}>
        {activeTab === "speisekarte" ? (
          <>
            {/* Page 1 */}
            <div className="flip-page">
              {/* FRONT COVER */}
              <div className="page-front cover-page no-padding">
                <div className="cover-full-image">
                  <img src="/images/menuback.jpg" alt="PAPADAM Cover" className="img-fluid w-100" />
                </div>
                <div className="cover-title-overlay position-absolute top-50 start-50 translate-middle text-center w-100 px-4">
                  <img src="/images/logo.png" alt="PAPADAM Restaurant Logo" className="img-fluid mx-auto d-block mb-3" style={{ maxWidth: "175px" }} />
                </div>
              </div>

              {/* BACK PAGE */}
              <div className="page-back">
                <div className="page-content h-100">
                  <div className="decorative-border"></div>
                  <div className="page-inner">
                    {renderCategory("SUPPEN")}
                    {renderCategory("SALATE")}
                    {renderCategory("VORSPEISEN", 4)}
                  </div>
                </div>
              </div>
            </div>

            {/* Page 2 - With VORSPEISEN first, then INDISCHE BROTSPEZIALITÄTEN */}
            <div className="flip-page">
              <div className="page-front">
                <div className="page-content h-100">
                  <div className="decorative-border"></div>
                  <div className="page-inner">
                    {/* Remaining VORSPEISEN items (after first 4) */}
                    {groupedItems["VORSPEISEN"] && groupedItems["VORSPEISEN"].items.length > 4 && (
                      <React.Fragment>
                        <h2 className="section-titles">VORSPEISEN</h2>
                        {groupedItems["VORSPEISEN"].description && (
                          <p className="category-description">{groupedItems["VORSPEISEN"].description}</p>
                        )}
                        {groupedItems["VORSPEISEN"].items.slice(4).map((item) => (
                          <div className="menu-item" key={item.dishid}>
                            <div className="d-flex justify-content-between align-items-baseline">
                              <span className="item-names fw-bold">{item.dishname}</span>
                              <span className="price fw-bold">{formatPrice(item.dishprice)}</span>
                            </div>
                            {item.dishdescription && (
                              <p className="item-description mb-0">{item.dishdescription}</p>
                            )}
                          </div>
                        ))}
                      </React.Fragment>
                    )}

                    {/* All INDISCHE BROTSPEZIALITÄTEN below */}
                    {renderCategory("INDISCHE BROTSPEZIALITÄTEN")}
                  </div>
                </div>
              </div>
              <div className="page-back">
                <div className="page-content h-100">
                  <div className="decorative-border"></div>
                  <div className="page-inner">
                    {groupedItems["VEGETARISCH"] && (
                      <React.Fragment>
                        <h2 className="section-titles">VEGETARISCH/Vegan</h2>
                        {groupedItems["VEGETARISCH"].description && (
                          <p className="category-description">{groupedItems["VEGETARISCH"].description}</p>
                        )}
                        {groupedItems["VEGETARISCH"].items.map((item) => (
                          <div className="menu-item" key={item.dishid}>
                            <div className="d-flex justify-content-between align-items-baseline">
                              <span className="item-names fw-bold">{item.dishname}</span>
                              <span className="price fw-bold">{formatPrice(item.dishprice)}</span>
                            </div>
                            {item.dishdescription && (
                              <p className="item-description mb-0">{item.dishdescription}</p>
                            )}
                          </div>
                        ))}
                      </React.Fragment>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Page 3 */}
            <div className="flip-page">
              <div className="page-front">
                <div className="page-content h-100">
                  <div className="decorative-border"></div>
                  <div className="page-inner">
                    {renderCategory("HÄHNCHENGERICHTE")}
                    {renderCategory("LAMMGERICHTE")}
                  </div>
                </div>
              </div>
              <div className="page-back">
                <div className="page-content h-100">
                  <div className="decorative-border"></div>
                  <div className="page-inner">
                    {renderCategory("FISCHGERICHTE")}
                    {renderCategory("PASTA")}
                  </div>
                </div>
              </div>
            </div>

            {/* Page 4 */}
            <div className="flip-page">
              <div className="page-front">
                <div className="page-content h-100">
                  <div className="decorative-border"></div>
                  <div className="page-inner">
                    {/* First 8 TANDOORI SPEZIALITÄTEN items */}
                    {groupedItems["TANDOORI SPEZIALITÄTEN"] && (
                      <React.Fragment>
                        <h2 className="section-titles">TANDOORI SPEZIALITÄTEN</h2>
                        {groupedItems["TANDOORI SPEZIALITÄTEN"].description && (
                          <p className="category-description">{groupedItems["TANDOORI SPEZIALITÄTEN"].description}</p>
                        )}
                        {groupedItems["TANDOORI SPEZIALITÄTEN"].items.slice(0, 8).map((item) => (
                          <div className="menu-item" key={item.dishid}>
                            <div className="d-flex justify-content-between align-items-baseline">
                              <span className="item-names fw-bold">{item.dishname}</span>
                              <span className="price fw-bold">{formatPrice(item.dishprice)}</span>
                            </div>
                            {item.dishdescription && (
                              <p className="item-description mb-0">{item.dishdescription}</p>
                            )}
                          </div>
                        ))}
                      </React.Fragment>
                    )}
                  </div>
                </div>
              </div>
              <div className="page-back">
                <div className="page-content h-100">
                  <div className="decorative-border"></div>
                  <div className="page-inner">
                    {/* Remaining TANDOORI SPEZIALITÄTEN items */}
                    {groupedItems["TANDOORI SPEZIALITÄTEN"] && groupedItems["TANDOORI SPEZIALITÄTEN"].items.length > 8 && (
                      <React.Fragment>
                        <h2 className="section-titles"></h2>
                        {groupedItems["TANDOORI SPEZIALITÄTEN"].items.slice(8).map((item) => (
                          <div className="menu-item" key={item.dishid}>
                            <div className="d-flex justify-content-between align-items-baseline">
                              <span className="item-names fw-bold">{item.dishname}</span>
                              <span className="price fw-bold">{formatPrice(item.dishprice)}</span>
                            </div>
                            {item.dishdescription && (
                              <p className="item-description mb-0">{item.dishdescription}</p>
                            )}
                          </div>
                        ))}
                      </React.Fragment>
                    )}
                    {/* REIS SPEZIALITÄTEN after TANDOORI SPEZIALITÄTEN */}
                    {renderCategory("REIS SPEZIALITÄTEN")}
                    {renderCategory("KINDER GERICHTE")}
                  </div>
                </div>
              </div>
            </div>

            {/* Page 5 */}
            <div className="flip-page">
              <div className="page-front">
                <div className="page-content h-100">
                  <div className="decorative-border"></div>
                  <div className="page-inner compact-page">
                    {renderCategory("NACHSPEISEN")}
                    {renderCategory("INDISCHE GETRÄNKE")}
                    {renderCategory("HEIßE GETRÄNKE")}

                    {/* Render any extra/custom Speisekarte categories added from admin */}
                    {Object.keys(groupedItems).map(label => {
                      if (!["SUPPEN", "SALATE", "VORSPEISEN", "INDISCHE BROTSPEZIALITÄTEN", "VEGETARISCH", "HÄHNCHENGERICHTE", "LAMMGERICHTE", "FISCHGERICHTE", "PASTA", "TANDOORI SPEZIALITÄTEN", "REIS SPEZIALITÄTEN", "KINDER GERICHTE", "NACHSPEISEN", "INDISCHE GETRÄNKE", "HEIßE GETRÄNKE"].includes(label)) {
                        return renderCategory(label);
                      }
                      return null;
                    })}
                  </div>
                </div>
              </div>

              <div className="page-back">
                <div className="page-content h-100">
                  <div className="decorative-border"></div>
                  <div className="page-inner allergen-page">
                    {renderAllergensAndAdditives()}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Page 1 (Cover + Vegetarisch) */}
            <div className="flip-page">
              {/* FRONT COVER */}
              <div className="page-front cover-page no-padding">
                <div className="cover-full-image">
                  <img src="/images/menuback.jpg" alt="PAPADAM Cover" className="img-fluid w-100" />
                </div>
                <div className="cover-title-overlay position-absolute top-50 start-50 translate-middle text-center w-100 px-4">
                  <img src="/images/logo.png" alt="PAPADAM Restaurant Logo" className="img-fluid mx-auto d-block mb-3" style={{ maxWidth: "175px" }} />
                </div>
              </div>

              {/* BACK PAGE: Vegetarisch + Hähnchengerichte + Lammgerichte */}
              <div className="page-back">
                <div className="page-content h-100">
                  <div className="decorative-border"></div>
                  <div className="page-inner compact-page">
                    <h1 className="text-center mb-1 text-danger fw-bold" style={{ fontSize: "1.15rem", fontFamily: "'Times New Roman', serif", letterSpacing: "1px" }}>Mittagskarte</h1>
                    <p className="text-center mb-2 text-dark fw-semibold" style={{ fontSize: "0.68rem" }}>Dienstag bis Freitag (von 11:00 bis 14:00 Uhr) Außer an Feiertagen</p>
                    {renderCategory("VEGETARISCH")}
                    {renderCategory("HÄHNCHENGERICHTE")}
                    {renderCategory("LAMMGERICHTE")}
                  </div>
                </div>
              </div>
            </div>

            {/* Page 2 (Indisches Brot + Salat on front, Allergene on back) */}
            <div className="flip-page">
              {/* FRONT PAGE: Indisches Brot & Salat combined */}
              <div className="page-front">
                <div className="page-content h-100">
                  <div className="decorative-border"></div>
                  <div className="page-inner compact-page">
                    {renderCategory("INDISCHES BROT")}
                    {renderCategory("SALAT")}
                  </div>
                </div>
              </div>

              {/* BACK PAGE: Kinder Gerichte + Allergene */}
              <div className="page-back">
                <div className="page-content h-100">
                  <div className="decorative-border"></div>
                  <div className="page-inner compact-page allergen-page">
                    {renderCategory("KINDER GERICHTE")}
                    {renderAllergensMittag()}
                  </div>
                </div>
              </div>
            </div>

            {/* Page 3 (Custom Categories) - only render if extra categories exist */}
            {Object.keys(groupedItems).some(label => !["VEGETARISCH", "HÄHNCHENGERICHTE", "LAMMGERICHTE", "INDISCHES BROT", "SALAT", "KINDER GERICHTE"].includes(label)) && (
              <div className="flip-page">
                {/* FRONT PAGE: Custom Categories */}
                <div className="page-front">
                  <div className="page-content h-100">
                    <div className="decorative-border"></div>
                    <div className="page-inner">
                      {/* Render any extra/custom Mittag categories added from admin */}
                      {Object.keys(groupedItems).map(label => {
                        if (!["VEGETARISCH", "HÄHNCHENGERICHTE", "LAMMGERICHTE", "INDISCHES BROT", "SALAT", "KINDER GERICHTE"].includes(label)) {
                          return (
                            <div key={label} style={{ marginTop: "15px" }}>
                              {renderCategory(label)}
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FlipBook;